import React, { Fragment, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    AppBar as MuiAppBar,
    Toolbar,
    Avatar,
    Chip,
    LinearProgress,
    Menu,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    Collapse,
    Drawer,
    Button,
} from '@material-ui/core/';
import withWidth, { isWidthUp, isWidthDown } from '@material-ui/core/withWidth';
import { withStyles } from '@material-ui/core/styles';
import FaceIcon from '@material-ui/icons/Face';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { NavLink } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';
import translationPropType from '../../../proptypes/translationPropType';
import getNavItems from './navItems';
import getStyles from './styles';
import AppBarHeader from './AppBarHeader';
import { openHelp, openSettings } from '../../../reduxStore/config/ui';
import { logout } from '../../../reduxStore/config/user';

const PROFILE_MENU = 'PROFILE_MENU';

const styles = theme => getStyles(theme);

const AppBar = ({
    classes,
    width,
    location,
    translation,
}) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState(null);
    const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(null);

    const companyStatsLocation = useSelector(state => state.companystats.location);
    const leadsStatsLocation = useSelector(state => state.leadsstats.location);
    const impressionStatsLocation = useSelector(state => state.impressionstats.location);
    const searchStatsLocation = useSelector(state => state.searchstats.location);
    const companyReportLocation = useSelector(state => state.companyreport.location);

    const isLoading = useSelector(state => state.ui.isLoading);
    const user = useSelector(state => state.user.user);

    const dispatch = useDispatch();

    const handleOpenHelp = () => {
        setOpenSubMenu(null);
        dispatch(openHelp);
    };

    const handleOpenSettings = () => {
        setOpenSubMenu(null);
        dispatch(openSettings);
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    const navItems = getNavItems(
        user,
        translation,
        companyReportLocation,
        searchStatsLocation,
        companyStatsLocation,
        leadsStatsLocation,
        impressionStatsLocation,
    );

    const NavLinkWithRef = React.forwardRef(function NavLinkWithRef(props, ref){ return <NavLink {...props} innerRef={ref} /> });

    const isSelected = (item) => {
        if (item.path) {
            return item.path === location.pathname;
        }
        if (item.subItems) {
            return !!item.subItems.find(subItem => subItem.path === location.pathname);
        }
        return false;
    };

    const handleSubMenuOpen = menuKey => (event) => {
        const newOpen = openSubMenu === menuKey ? null : menuKey;
        setSubMenuAnchorEl(event.currentTarget);
        setOpenSubMenu(newOpen);
    };

    const handleSubMenuClose = () => {
        setSubMenuAnchorEl(null);
        setOpenSubMenu(null);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleDrawerOpen = () => {
        setDrawerOpen(true);
    };

    const renderProfileMenu = (
        <div className={classes.profileMenuWrapper}>
            {isWidthDown('xs', width) ? (
                <Avatar
                    className={classes.smallAvatar}
                    alt={user.fullName}
                    onClick={handleSubMenuOpen(PROFILE_MENU)}
                >
                    <FaceIcon />
                </Avatar>
            ) : (
                <Chip
                    avatar={
                        (
                            <Avatar>
                                <FaceIcon />
                            </Avatar>
                        )
                    }
                    color="default"
                    label={user.fullName}
                    onClick={handleSubMenuOpen(PROFILE_MENU)}
                />
            )}
            <Menu
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={openSubMenu === PROFILE_MENU}
                anchorEl={subMenuAnchorEl}
                onClose={handleSubMenuClose}
            >
                <MenuItem
                    onClick={handleOpenSettings}
                >
                    {translation.SETTINGS}
                </MenuItem>
                <MenuItem
                    onClick={handleOpenHelp}
                >
                    {translation.HELP}
                </MenuItem>
                <MenuItem
                    onClick={handleLogout}
                >
                    {translation.LOG_OUT}
                </MenuItem>
            </Menu>
        </div>
    );

    const renderSubMenu = (subItems, menuKey) => (
        <Menu
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={openSubMenu === menuKey}
            anchorEl={subMenuAnchorEl}
            onClose={handleSubMenuClose}
        >
            {subItems.map(item => (
                <MenuItem
                    onClick={handleSubMenuClose}
                    component={NavLinkWithRef}
                    key={item.key}
                    to={item.location}
                    {...(item.path && { activeClassName: classes.activeLink })}
                >
                    {item.text}
                </MenuItem>
            ))}
        </Menu>
    );

    const renderExpandIcon = isOpen => (
        <>
            { isOpen ? (<ExpandLess />) : (<ExpandMore />)}
        </>
    );

    const renderDesktopNavButtons = (
        <>
            {isWidthUp('md', width) && (
                <div>
                    {navItems.map(item => item.visible && (
                        <Button
                            className={isSelected(item)
                                ? classes.activeButton
                                : classes.button}
                            component={item.path
                                ? NavLinkWithRef
                                : Button}
                            to={item.location}
                            key={item.key}
                            color={isSelected(item) ? 'primary' : 'default'}
                            style={isSelected(item) ? { backgroundColor: 'transparent' } : {}}
                            onClick={item.subItems ? handleSubMenuOpen(item.key) : null}
                            {...(item.path && { activeClassName: classes.activeLink })}
                        >
                            {item.text}
                            {item.subItems
                                && (
                                    <>
                                        <ExpandMore className={classes.rightIcon} />
                                        {renderSubMenu(item.subItems, item.key)}
                                    </>
                                )}
                        </Button>
                    ))
                    }
                </div>
            )}
        </>
    );

    const renderMobileDrawer = (
        <Drawer
            className={classes.drawer}
            open={drawerOpen}
            onClose={handleDrawerClose}
            classes={{
                paper: classes.drawerPaper,
            }}
        >
            <List
                tabIndex={0}
                role="button"
            >
                {navItems.map(item => item.visible && (
                    <Fragment key={item.key}>
                        <ListItem
                            component={item.path ? NavLinkWithRef : ListItem}
                            button
                            to={item.location}
                            onClick={item.subItems ? handleSubMenuOpen(item.key) : null}
                            selected={isSelected(item)}
                            {...(item.path && { activeClassName: classes.activeLink })}
                        >
                            <ListItemText primary={item.text} />
                            {item.subItems ? renderExpandIcon(openSubMenu === item.key) : null}
                        </ListItem>
                        {item.subItems
                                    && (
                                        <Collapse
                                            in={openSubMenu === item.key}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List
                                                component="div"
                                                className={classes.drawerSubList}
                                            >
                                                {item.subItems.map(subItem => (
                                                    <ListItem
                                                        className={classes.drawerSubListItem}
                                                        component={NavLinkWithRef}
                                                        button
                                                        to={subItem.location}
                                                        key={subItem.key}
                                                        {...(subItem.path
                                                            && {
                                                                activeClassName: classes.activeLink,
                                                            }
                                                        )}
                                                    >
                                                        <ListItemText primary={subItem.text} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Collapse>
                                    )}
                    </Fragment>
                ))}
            </List>
        </Drawer>
    );

    return (
        <div className={classes.root}>
            {renderMobileDrawer}
            <MuiAppBar
                position="static"
                color="default"
                className={classes.appBar}
            >
                <Toolbar>
                    <AppBarHeader
                        translation={translation}
                        handleDrawerOpen={handleDrawerOpen}
                    />
                    <div className={classes.grow} />
                    {renderDesktopNavButtons}
                    {renderProfileMenu}
                </Toolbar>
                { isLoading && <LinearProgress className={classes.progress} /> }
            </MuiAppBar>
        </div>
    );
};

AppBar.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    width: PropTypes.string.isRequired,
    location: ReactRouterPropTypes.location.isRequired,
};

export default withWidth()(withStyles(styles)(AppBar));
